import { useState, useEffect, useCallback } from 'react';
import { Clock, CheckCircle2, XCircle, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import bookingsApi, { type Booking } from '@/api/bookings';

function formatLocalDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString();
}

interface Props {
  onChange?: () => void;
}

export function PendingConfirmationsBanner({ onChange }: Props) {
  const [pending, setPending] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actingId, setActingId] = useState<string | null>(null);
  const [decliningFor, setDecliningFor] = useState<Booking | null>(null);
  const [declineReason, setDeclineReason] = useState('');

  const fetchPending = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await bookingsApi.list({ limit: 50 });
      setPending(res.bookings.filter((b) => b.awaitingConfirmation));
    } catch (err) {
      console.error('Failed to fetch pending confirmations:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleConfirm = async (booking: Booking) => {
    setActingId(booking.id);
    try {
      await bookingsApi.confirm(booking.id);
      toast.success('Appointment confirmed');
      setPending((prev) => prev.filter((b) => b.id !== booking.id));
      onChange?.();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to confirm appointment');
    } finally {
      setActingId(null);
    }
  };

  const handleDeclineSubmit = async () => {
    if (!decliningFor) return;
    setActingId(decliningFor.id);
    try {
      await bookingsApi.decline(decliningFor.id, declineReason.trim() || undefined);
      toast.success('Appointment declined');
      setPending((prev) => prev.filter((b) => b.id !== decliningFor.id));
      setDecliningFor(null);
      setDeclineReason('');
      onChange?.();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to decline appointment');
    } finally {
      setActingId(null);
    }
  };

  if (isLoading || pending.length === 0) return null;

  return (
    <>
      <div className="rounded-xl border border-yellow-200 dark:border-yellow-500/30 bg-yellow-50 dark:bg-yellow-900/20 p-4 mb-6">
        <div className="flex items-start gap-3 mb-3">
          <div className="shrink-0 w-9 h-9 rounded-full bg-yellow-100 dark:bg-yellow-800/40 flex items-center justify-center">
            <Clock className="w-5 h-5 text-yellow-700 dark:text-yellow-300" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-yellow-900 dark:text-yellow-100">
              {pending.length} appointment{pending.length > 1 ? 's' : ''} awaiting your confirmation
            </h3>
            <p className="text-xs text-yellow-800 dark:text-yellow-200/80 mt-0.5">
              A professional scheduled these for you. Please review and accept or decline.
            </p>
          </div>
        </div>

        <ul className="space-y-2">
          {pending.map((b) => (
            <li
              key={b.id}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white dark:bg-[#16151D] rounded-lg border border-yellow-100 dark:border-yellow-500/20 px-3 py-2.5"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-900 dark:text-white truncate">
                  <span className="font-medium">{b.petName}</span>
                  {b.serviceName ? <> — {b.serviceName}</> : null}
                </p>
                <p className="text-xs text-gray-600 dark:text-white/60 mt-0.5">
                  {formatLocalDate(b.scheduledDate)} at {b.scheduledTime}
                  {b.businessName ? ` · ${b.businessName}` : ''}
                </p>
              </div>
              <div className="flex gap-2 shrink-0 w-full sm:w-auto">
                <button
                  type="button"
                  disabled={actingId === b.id}
                  onClick={() => handleConfirm(b)}
                  className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg disabled:opacity-50 shadow-sm"
                  style={{ backgroundColor: '#059669', color: '#ffffff' }}
                >
                  {actingId === b.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4" />
                  )}
                  Accept
                </button>
                <button
                  type="button"
                  disabled={actingId === b.id}
                  onClick={() => setDecliningFor(b)}
                  className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border border-red-300 dark:border-red-500/30 text-red-700 dark:text-red-300 bg-white dark:bg-transparent hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                >
                  <XCircle className="w-4 h-4" />
                  Decline
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {decliningFor && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setDecliningFor(null);
              setDeclineReason('');
            }
          }}
        >
          <div className="bg-white dark:bg-[#16151D] rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Decline appointment
                </h3>
                <p className="text-xs text-gray-500 dark:text-white/60 mt-1">
                  {decliningFor.petName} — {formatLocalDate(decliningFor.scheduledDate)} at {decliningFor.scheduledTime}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setDecliningFor(null);
                  setDeclineReason('');
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <label className="block text-xs font-medium text-gray-700 dark:text-white/80 mb-1">
              Reason (optional)
            </label>
            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Let the provider know why…"
              className="w-full px-3 py-2 border border-gray-300 dark:border-white/10 rounded-lg text-sm bg-white dark:bg-[#16151D] text-gray-900 dark:text-white resize-none"
              rows={3}
            />

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setDecliningFor(null);
                  setDeclineReason('');
                }}
                className="px-4 py-2 text-sm text-gray-700 dark:text-white/80 hover:bg-gray-100 dark:hover:bg-white/5 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={actingId === decliningFor.id}
                onClick={handleDeclineSubmit}
                className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 inline-flex items-center gap-2"
              >
                {actingId === decliningFor.id && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Decline appointment
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
